import { useAuthenticatedUser } from '@contexts/auth-provider';
import { useOrganizations } from '@contexts/organizations-provider';
import { usePermissions } from '@contexts/permissions-provider';
import { ContentLoader } from '@shared/components/content-loader/ContentLoader';
import { InlineFeedback } from '@shared/components/inline-feedback/InlineFeedback';
import { useGetToken } from '@shared/hooks/use-get-token';
import * as React from 'react';

import type { CustomerDocumentsRouterProps } from './CustomerDocumentsRouterPage.types';

import styles from './CustomerDocumentsRouterPage.module.css';

/**
 * CustomerDocumentsRouterPage - MFE wrapper for the Manage "[Customer name]" documents entry
 *
 * This page loads the remote CustomerDocumentsRouter MFE and passes
 * the required props. The MFE handles its own internal routing.
 *
 * Guard stack (applied in manage.routes.tsx):
 * 1. AuthenticatedRoute - User must be logged in
 * 2. OrganizationRouteGuard - Validate org access
 * 3. FeatureFlagGuard - manageViewCustomerNonFaaDocuments must be enabled
 * 4. PermissionGuard - CustomerTrainingDocument_View_Org required
 */
const CustomerDocumentsRouterPage = React.memo(
  (): React.ReactElement | null => {
    const authData = useAuthenticatedUser();
    const { selectedOrganization } = useOrganizations();
    const { permissions } = usePermissions();
    const getToken = useGetToken();

    const [RemoteRouter, setRemoteRouter] =
      React.useState<React.ComponentType<CustomerDocumentsRouterProps> | null>(
        null
      );
    const [loadError, setLoadError] = React.useState<Error | null>(null);

    React.useEffect(() => {
      const loadRouter = async (): Promise<void> => {
        try {
          const { default: Router } =
            await import('document_center_mfe/CustomerDocumentsRouter');
          setRemoteRouter(() => Router);
        } catch (error) {
          console.error('Error loading CustomerDocumentsRouter:', error);
          setLoadError(error as Error);
        }
      };

      loadRouter();
    }, []);

    if (loadError) {
      return <InlineFeedback type="internal-server-error" showGoHome={false} />;
    }

    if (!RemoteRouter || !selectedOrganization) {
      return <ContentLoader />;
    }

    return (
      <div
        data-testid="customer-documents-router-page"
        className={styles.customerDocumentsRouterPage}
      >
        <React.Suspense fallback={<ContentLoader />}>
          <RemoteRouter
            getToken={getToken}
            permissions={permissions}
            title={selectedOrganization.name}
            organizationId={selectedOrganization.id}
            user={authData}
          />
        </React.Suspense>
      </div>
    );
  }
);

CustomerDocumentsRouterPage.displayName = 'CustomerDocumentsRouterPage';

export { CustomerDocumentsRouterPage };
